import { clsx } from 'clsx'
import { CloudUpload } from 'lucide-react'
import { ComponentProps, RefObject, useCallback, useEffect, useState } from 'react'
import { useDropzone } from 'react-dropzone'
import { ModalProvider } from '../../providers'
import { getComposerFileStore, useWebchatStore } from '../../stores'
import { webchatClasses } from '../../styles/classes'
import { FileType } from '../../types'

type Props = ComponentProps<'div'> & {
  connected?: boolean
  uploadFile?: (file: File) => Promise<{ fileUrl: string; name: string; type: FileType }>
  allowFileUpload?: boolean
  storageKey?: string
}

export const Container = ({
  ref,
  children,
  className,
  uploadFile,
  allowFileUpload,
  storageKey,
  connected: _,
  ...props
}: Props) => {
  const { container } = webchatClasses

  const setMessageContainerRef = useWebchatStore((s) => s.setMessageContainerRef)

  const useComposerFileStore = getComposerFileStore(storageKey)
  const uploadComposerFile = useComposerFileStore((state) => state.uploadFile)
  const [dragDepth, setDragDepth] = useState(0)

  useEffect(() => {
    setMessageContainerRef(ref as RefObject<HTMLDivElement>)
  }, [ref, setMessageContainerRef])

  const onDrop = useCallback(
    (files: File[]) => {
      if (!uploadFile) return
      files.forEach((file) => uploadComposerFile(file, uploadFile))
      setDragDepth(0)
    },
    [uploadComposerFile, uploadFile]
  )

  const { getRootProps } = useDropzone({
    noClick: true,
    noKeyboard: true,
    onDrop,
    disabled: !(allowFileUpload && uploadFile),
    onDragEnter: () => setDragDepth((prev) => prev + 1),
    onDragLeave: () => setDragDepth((prev) => prev - 1),
  })

  return (
    <div
      {...props}
      className={clsx(container.className, className)}
      ref={ref}
      {...(getRootProps() as ComponentProps<'div'>)}
    >
      <div data-hover={dragDepth > 0} {...container.dropzone.overlay}>
        Drop files to upload
        <CloudUpload style={{ width: '50px' }} />
      </div>
      <ModalProvider>{children}</ModalProvider>
    </div>
  )
}

Container.displayName = 'Container'
